import {
  Activity,
  AlertTriangle,
  ClipboardList,
  HelpCircle,
  LayoutDashboard,
  LogOut,
  Shield,
  UserPlus,
  Wifi,
  WifiOff,
} from 'lucide-react';
import React from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';

import { BrandMark } from '@/components/BrandMark';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { OfflineBanner } from '@/components/guard/OfflineBanner';
import { LockdownBanner } from '@/components/guard/LockdownBanner';
import { ShiftHeader } from '@/components/guard/ShiftHeader';
import { useAuth } from '@/contexts/AuthContext';
import { useGuardContext } from '@/hooks/useGuardContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslation } from '@/hooks/useTranslation';

const GUARD_NAV = [
  { to: '/guard', labelKey: 'guard.nav.gateQueue', icon: LayoutDashboard, end: true },
  { to: '/guard/walk-in', labelKey: 'guard.nav.walkIn', icon: UserPlus, end: false },
  { to: '/guard/incidents', labelKey: 'guard.nav.incidents', icon: AlertTriangle, end: false },
  { to: '/guard/activity', labelKey: 'guard.nav.activityLog', icon: ClipboardList, end: false },
] as const;

function initials(name: string): string {
  return name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

// Closes the mobile off-canvas sidebar on tap so the chosen page shows at once.
function GuardNavLink({ item }: { item: (typeof GUARD_NAV)[number] }) {
  const { t } = useTranslation();
  const { isMobile, setOpenMobile } = useSidebar();
  return (
    <SidebarMenuButton asChild tooltip={t(item.labelKey)}>
      <NavLink
        to={item.to}
        end={item.end === true}
        onClick={() => {
          if (isMobile) setOpenMobile(false);
        }}
        className={({ isActive }) =>
          isActive ? 'text-sidebar-primary-foreground bg-sidebar-accent' : ''
        }
      >
        <item.icon className="size-4" />
        <span>{t(item.labelKey)}</span>
      </NavLink>
    </SidebarMenuButton>
  );
}

// ─── Error boundary (prevents blank screen on render errors) ─────────────────

interface ErrorBoundaryState {
  hasError: boolean;
}

class GuardErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-8 text-center">
          <AlertTriangle className="size-10 text-[var(--status-amber-text)]" />
          <p className="text-sm font-medium text-[var(--text-primary)]">Something went wrong</p>
          <p className="text-xs text-[var(--text-secondary)]">Refresh the page to try again.</p>
          <button
            onClick={() => window.location.reload()}
            className="rounded-md border border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-surface-raised)]"
          >
            Refresh
          </button>
        </div>
      );
    }
    return <>{this.props.children}</>;
  }
}

export function GuardLayout() {
  const { user, signOut } = useAuth();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { guardName, gateName, propertyName, isOnline, activeShift } = useGuardContext();
  const location = useLocation();

  // Enforce shift requirement: guards must start a shift before accessing gate operations.
  // Without an active shift there's no gate context, so walk-ins and access events
  // would be created without a gate_id — causing data quality issues.
  const isShiftPage =
    location.pathname === '/guard/shift-start' ||
    location.pathname === '/guard/shift-selection' ||
    location.pathname === '/guard/select';
  const isDeviceSession = !!localStorage.getItem('sg_device_session');
  if (!activeShift && !isShiftPage) {
    // Mode B (shared device) → redirect to roster selection page where guard
    // picks their name. Mode A (personal device) → redirect to shift-start
    // where guard selects a gate and starts shift via their own JWT.
    return <Navigate to={isDeviceSession ? '/guard/select' : '/guard/shift-start'} replace />;
  }

  const displayGuardName = guardName ?? user?.fullName ?? 'Guard';
  const displayGateName = gateName || t('guard.shift.selectGatePlaceholder');
  const displayPropertyName = propertyName || 'Estate';

  const handleSignOut = () => {
    void signOut();
  };

  if (isMobile) {
    return (
      <div className="flex h-dvh flex-col bg-[var(--bg-page)]">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-4">
          <Shield className="size-5 text-[var(--text-accent)]" />
          <div className="flex flex-col leading-tight flex-1 min-w-0">
            {activeShift ? (
              <span className="truncate text-sm font-medium text-[var(--text-primary)]">
                {displayGateName}
              </span>
            ) : (
              <Link
                to="/guard/shift-start"
                className="truncate text-sm font-medium text-[var(--text-accent)] underline underline-offset-2"
                aria-label={t('guard.shift.selectGatePlaceholder')}
              >
                {displayGateName}
              </Link>
            )}
            <span className="truncate text-xs text-[var(--text-secondary)]">
              {displayPropertyName}
            </span>
          </div>
          <ShiftHeader />
          {isOnline ? (
            <Wifi
              className="size-4 text-[var(--status-green-text)]"
              aria-label={t('guard.header.online')}
            />
          ) : (
            <WifiOff
              className="size-4 text-[var(--status-amber-text)]"
              aria-label={t('guard.header.offline')}
            />
          )}
        </header>
        <OfflineBanner />
        <LockdownBanner />
        <main className="flex-1 overflow-auto p-4">
          <GuardErrorBoundary>
            <Outlet />
          </GuardErrorBoundary>
        </main>
        <nav
          className="flex shrink-0 border-t border-border bg-[var(--bg-surface)]"
          aria-label={t('guard.nav.gateQueue')}
        >
          {GUARD_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end === true}
              className={({ isActive }) =>
                `flex flex-1 flex-col items-center justify-center gap-1 py-3 min-h-[44px] min-w-[44px] transition-colors ${
                  isActive
                    ? 'text-[var(--text-accent)]'
                    : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                }`
              }
            >
              <item.icon className="size-5" />
              <span className="text-[10px] font-medium leading-tight">{t(item.labelKey)}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <Sidebar variant="inset" collapsible="icon">
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" asChild>
                <Link to="/guard">
                  <BrandMark className="size-8 shrink-0" aria-hidden />
                  <div className="flex flex-col leading-tight">
                    <span className="font-semibold tracking-tight text-sm">
                      {t('guard.brand.title')}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t('guard.brand.subtitle')}
                    </span>
                  </div>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {GUARD_NAV.map((item) => (
                  <SidebarMenuItem key={item.to}>
                    <GuardNavLink item={item} />
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => {
                  if (window.Intercom) {
                    window.Intercom('show');
                  } else {
                    window.open('mailto:support@securelabsystems.com', '_blank');
                  }
                }}
                className="text-muted-foreground hover:text-foreground cursor-pointer"
              >
                <HelpCircle className="size-4" />
                <span>{t('common.helpSupport')}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => window.open('https://status.securelabsystems.com', '_blank')}
                className="text-muted-foreground hover:text-foreground cursor-pointer"
              >
                <Activity className="size-4" />
                <span>{t('common.systemStatus')}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton size="lg">
                    <Avatar className="size-8 shrink-0">
                      <AvatarFallback className="bg-primary/20 text-[var(--avatar-initials-fg)] text-xs font-semibold">
                        {user ? initials(user.fullName) : '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col items-start min-w-0 flex-1">
                      <span className="truncate text-sm font-medium">{user?.fullName}</span>
                      <span className="truncate text-xs text-muted-foreground">{user?.email}</span>
                    </div>
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="start" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col gap-1">
                      <p className="text-sm font-medium">{user?.fullName}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleSignOut}
                    className="text-destructive focus:text-destructive"
                  >
                    <LogOut className="mr-2 size-4" />
                    {t('auth.signOut')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
      <SidebarInset>
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-4">
          <SidebarTrigger className="-ml-1 min-w-[44px] min-h-[44px]" />
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="truncate text-sm font-medium text-[var(--text-primary)]">
              {displayGuardName}
            </span>
            <span className="text-[var(--border-default)]">·</span>
            {activeShift ? (
              <span className="truncate text-sm text-[var(--text-secondary)]">
                {displayGateName}
              </span>
            ) : (
              <Link
                to="/guard/shift-start"
                className="truncate text-sm text-[var(--text-accent)] underline underline-offset-2"
                aria-label={t('guard.shift.selectGatePlaceholder')}
              >
                {displayGateName}
              </Link>
            )}
            <span className="text-[var(--border-default)]">·</span>
            <span className="truncate text-sm text-[var(--text-secondary)]">
              {displayPropertyName}
            </span>
          </div>
          <ShiftHeader />
          {isOnline ? (
            <span className="flex items-center gap-1.5 text-xs text-[var(--status-green-text)]">
              <Wifi className="size-4" />
              <span className="hidden sm:inline">{t('guard.header.online')}</span>
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-xs text-[var(--status-amber-text)]">
              <WifiOff className="size-4" />
              <span className="hidden sm:inline">{t('guard.header.offline')}</span>
            </span>
          )}
        </header>
        <OfflineBanner />
        <LockdownBanner />
        <main className="flex-1 overflow-auto p-6">
          <GuardErrorBoundary>
            <Outlet />
          </GuardErrorBoundary>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
